import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../../Common/isRequestCanceled";
import {
  BASE_URL,
  EMPLOYEE_SALES_PER_CATEGORY_REPORT,
  NET_SALES_BY_EMPLOYEE_REPORT,
} from "../../../../Constants/Config";
const initialState = {
  loading: false,
  loadingMoreByEmp: {},
  reportRequestId: 0,
  EmployeeSalesPerCategoryData: [],
  successMessage: "",
  error: "",
  status: false,
};

const getEmployeeItemRows = (value) => {
  if (Array.isArray(value)) {
    return value.flat().filter(Boolean);
  }
  if (value && typeof value === "object" && Array.isArray(value.data)) {
    return value.data.flat().filter(Boolean);
  }
  return [];
};

const mergeEmployeeEntry = (previous, incoming) => {
  const previousRows = getEmployeeItemRows(previous);
  const incomingRows = getEmployeeItemRows(incoming);
  const mergedRows = [...previousRows, ...incomingRows];
  if (incoming && typeof incoming === "object" && !Array.isArray(incoming)) {
    return {
      ...incoming,
      data: [mergedRows],
      has_more: incomingRows.length
        ? incoming.has_more === true || incoming.has_more === "true"
        : false,
    };
  }
  return [mergedRows];
};

let abortEmployeeSalesPerCategoryData = null;
let abortNetSalesByEmployeeReport = null;
const abortLoadMoreByEmp = {};
const inFlightPageByEmp = {};

const abortAllLoadMoreRequests = () => {
  Object.keys(abortLoadMoreByEmp).forEach((empKey) => {
    abortLoadMoreByEmp[empKey]?.abort();
    delete abortLoadMoreByEmp[empKey];
    delete inFlightPageByEmp[empKey];
  });
};

// Generate pening , fulfilled and rejected action type
export const fetchEmployeeSalesPerCategoryData = createAsyncThunk(
  "EmployeeSalesPerCategorySlice/fetchEmployeeSalesPerCategoryData.",
  async (data, { rejectWithValue }) => {
    try {
      if (abortEmployeeSalesPerCategoryData) {
        abortEmployeeSalesPerCategoryData.abort();
      }

      abortEmployeeSalesPerCategoryData = new AbortController();
      const signal = abortEmployeeSalesPerCategoryData.signal;

      const { token, ...newData } = data;
      const response = await axios.post(
        BASE_URL + EMPLOYEE_SALES_PER_CATEGORY_REPORT,
        newData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );
      if (response.data.status === true) {
        // console.log(response.data);
        return response.data.report_data;
      }
    } catch (error) {
      // throw new Error(error.response.data.message);
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);
// Generate pening , fulfilled and rejected action type
export const NetSalesByEmployeeReport = createAsyncThunk(
  "EmployeeSalesPerCategorySlice/NetSalesByEmployeeReport.",
  async (data, { rejectWithValue }) => {
    try {
      if (abortNetSalesByEmployeeReport) {
        abortNetSalesByEmployeeReport.abort();
      }
      abortAllLoadMoreRequests();

      abortNetSalesByEmployeeReport = new AbortController();
      const signal = abortNetSalesByEmployeeReport.signal;

      const { token, empKey, requestId, ...newData } = data;
      const response = await axios.post(
        BASE_URL + NET_SALES_BY_EMPLOYEE_REPORT,
        newData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );
      if (response?.data?.status === true) {
        const obj = response?.data?.result;
        const status = response?.data?.status;
        return { obj, status };
      } else {
        const obj = {};
        const status = false;
        return { obj, status };
      }
    } catch (error) {
      // throw new Error(error.response.data.message);
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const NetSalesByEmployeeReportLoadMore = createAsyncThunk(
  "EmployeeSalesPerCategorySlice/NetSalesByEmployeeReportLoadMore",
  async (data, { rejectWithValue }) => {
    const empKey = data?.empKey;
    const page = Number(data?.page) || 1;
    const controller = new AbortController();
    try {
      if (empKey && abortLoadMoreByEmp[empKey]) {
        if (inFlightPageByEmp[empKey] === page) {
          abortLoadMoreByEmp[empKey].abort();
        } else {
          return rejectWithValue({
            message: "canceled",
            status: "canceled",
            empKey,
          });
        }
      }
      if (empKey) {
        abortLoadMoreByEmp[empKey] = controller;
        inFlightPageByEmp[empKey] = page;
      }
      const { token, empKey: _empKey, requestId, ...newData } = data;
      const response = await axios.post(
        BASE_URL + NET_SALES_BY_EMPLOYEE_REPORT,
        newData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        }
      );
      if (response?.data?.status === true) {
        return {
          obj: response?.data?.result,
          status: true,
          empKey,
          requestId,
          page,
        };
      }
      return { obj: {}, status: false, empKey, requestId, page };
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
        empKey,
      };
      return rejectWithValue(customError);
    } finally {
      if (empKey && abortLoadMoreByEmp[empKey] === controller) {
        delete abortLoadMoreByEmp[empKey];
        delete inFlightPageByEmp[empKey];
      }
    }
  }
);

const EmployeeSalesPerCategorySlice = createSlice({
  name: "EmployeeSalesPerCategorySlice",
  initialState,
  reducers: {
    stateUpdateOfEmployeeSalesCategory: (state) => {
      state.EmployeeSalesPerCategoryData = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEmployeeSalesPerCategoryData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      fetchEmployeeSalesPerCategoryData.fulfilled,
      (state, action) => {
        state.loading = false;
        state.EmployeeSalesPerCategoryData = action.payload;
        state.error = "";
      }
    );
    builder.addCase(
      fetchEmployeeSalesPerCategoryData.rejected,
      (state, action) => {
        state.loading = false;
        state.EmployeeSalesPerCategoryData = {};
        state.error = action.error.message;
      }
    );
    // NetSalesByReport
    builder.addCase(NetSalesByEmployeeReport.pending, (state, action) => {
      state.loading = true;
      state.loadingMoreByEmp = {};
      state.reportRequestId = action.meta.arg?.requestId || 0;
      state.EmployeeSalesPerCategoryData = {};
      state.error = "";
    });
    builder.addCase(NetSalesByEmployeeReport.fulfilled, (state, action) => {
      state.loading = false;
      state.EmployeeSalesPerCategoryData = action.payload.obj;
      state.status = action.payload.status;
      state.error = "";
    });
    builder.addCase(NetSalesByEmployeeReport.rejected, (state, action) => {
      state.loading = false;
      state.EmployeeSalesPerCategoryData = {};
      state.error = action.error.message;
    });
    builder.addCase(NetSalesByEmployeeReportLoadMore.pending, (state, action) => {
      const empKey = action.meta.arg?.empKey;
      if (empKey) {
        state.loadingMoreByEmp[empKey] = true;
      }
    });
    builder.addCase(
      NetSalesByEmployeeReportLoadMore.fulfilled,
      (state, action) => {
        const empKey = action.payload?.empKey;
        const incoming = action.payload?.obj || {};
        if (empKey) {
          state.loadingMoreByEmp[empKey] = false;
        }
        if (!empKey) {
          return;
        }
        if (
          action.meta.arg?.requestId !== undefined &&
          action.meta.arg.requestId !== state.reportRequestId
        ) {
          return;
        }
        const currentEntry = state.EmployeeSalesPerCategoryData?.[empKey];
        if (!currentEntry) {
          return;
        }
        const currentEmpId =
          currentEntry &&
          typeof currentEntry === "object" &&
          !Array.isArray(currentEntry)
            ? currentEntry.emp_id
            : "";
        const nextEntry =
          incoming[empKey] ||
          Object.values(incoming).find(
            (value) =>
              value &&
              typeof value === "object" &&
              !Array.isArray(value) &&
              currentEmpId &&
              String(value.emp_id) === String(currentEmpId)
          );
        const currentPage =
          currentEntry &&
          typeof currentEntry === "object" &&
          !Array.isArray(currentEntry)
            ? Number(currentEntry.page) || 1
            : 1;
        const incomingPage =
          Number(nextEntry?.page) || Number(action.payload?.page) || 0;
        if (incomingPage && incomingPage <= currentPage) {
          return;
        }
        if (nextEntry) {
          state.EmployeeSalesPerCategoryData[empKey] = mergeEmployeeEntry(
            currentEntry,
            nextEntry
          );
        } else if (
          currentEntry &&
          typeof currentEntry === "object" &&
          !Array.isArray(currentEntry)
        ) {
          state.EmployeeSalesPerCategoryData[empKey] = {
            ...currentEntry,
            has_more: false,
          };
        }
      }
    );
    builder.addCase(
      NetSalesByEmployeeReportLoadMore.rejected,
      (state, action) => {
        if (action.payload?.status === "canceled") {
          return;
        }
        const empKey = action.meta.arg?.empKey || action.payload?.empKey;
        if (empKey) {
          state.loadingMoreByEmp[empKey] = false;
        }
      }
    );
  },
});

export const { stateUpdateOfEmployeeSalesCategory } =
  EmployeeSalesPerCategorySlice.actions;
export default EmployeeSalesPerCategorySlice.reducer;
